import { useState, useEffect } from "react";
import { Layout } from "../components/Layout";
import { apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Tag, Loader2, Palette } from "lucide-react";

const PRESET_COLORS = [
  "#3B82F6", // Blue
  "#8B5CF6", // Purple
  "#10B981", // Green
  "#F59E0B", // Amber
  "#EF4444", // Red
  "#EC4899", // Pink
  "#06B6D4", // Cyan
  "#6366F1", // Indigo
  "#14B8A6", // Teal
  "#F97316", // Orange
];

export default function TagsPage() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    color: PRESET_COLORS[0],
  });

  const fetchTags = async () => {
    try {
      const res = await apiClient.get("/tags");
      setTags(res.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Tags");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, []);

  const resetForm = () => {
    setFormData({
      name: "",
      color: PRESET_COLORS[0],
    });
    setSelectedTag(null);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Name ist erforderlich");
      return;
    }

    setSaving(true);
    try {
      if (selectedTag) {
        await apiClient.put(`/tags/${selectedTag.id}`, formData);
        toast.success("Tag aktualisiert");
      } else {
        await apiClient.post("/tags", formData);
        toast.success("Tag erstellt");
      }
      setShowModal(false);
      resetForm();
      fetchTags();
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Speichern";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tag) => {
    if (!window.confirm(`Sind Sie sicher, dass Sie den Tag "${tag.name}" löschen möchten?`)) {
      return;
    }

    try {
      await apiClient.delete(`/tags/${tag.id}`);
      toast.success("Tag gelöscht");
      fetchTags();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const openEditModal = (tag) => {
    setSelectedTag(tag);
    setFormData({
      name: tag.name,
      color: tag.color,
    });
    setShowModal(true);
  };

  const getTagStyle = (color) => ({
    backgroundColor: `${color}20`,
    color: color,
    borderColor: `${color}40`,
  });

  return (
    <Layout title="Tags">
      {/* Header */}
      <div className="flex justify-between items-center mb-6" data-testid="tags-header">
        <p className="text-slate-500">
          Verwalten Sie Ihre Tags für die Kundenorganisation
        </p>
        <Button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-indigo-600 hover:bg-indigo-700 shadow-sm"
          data-testid="add-tag-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Neuer Tag
        </Button>
      </div>

      {/* Tags Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : tags.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200" data-testid="tags-empty">
          <Tag className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Keine Tags vorhanden</h3>
          <p className="text-slate-500 mb-4">Erstellen Sie Ihren ersten Tag zur Kundenorganisation</p>
          <Button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ersten Tag erstellen
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="tags-grid">
          {tags.map((tag) => (
            <div
              key={tag.id}
              className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow duration-200"
              data-testid={`tag-card-${tag.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <Badge
                  variant="outline"
                  className="text-sm px-3 py-1"
                  style={getTagStyle(tag.color)}
                >
                  {tag.name}
                </Badge>
                <div
                  className="w-6 h-6 rounded-full border-2 border-white shadow-sm"
                  style={{ backgroundColor: tag.color }}
                />
              </div>

              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => openEditModal(tag)}
                  data-testid={`edit-tag-${tag.id}`}
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Bearbeiten
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDelete(tag)}
                  data-testid={`delete-tag-${tag.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md" data-testid="tag-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {selectedTag ? "Tag bearbeiten" : "Neuer Tag"}
            </DialogTitle>
            <DialogDescription>
              {selectedTag
                ? "Aktualisieren Sie die Tag-Informationen"
                : "Erstellen Sie einen neuen Tag"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="tag-name">Name *</Label>
              <Input
                id="tag-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. VIP, Interessent, etc."
                data-testid="tag-name-input"
              />
            </div>

            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Palette className="w-4 h-4 text-slate-400" />
                Farbe
              </Label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                      formData.color === color
                        ? "border-slate-900 scale-110"
                        : "border-transparent"
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setFormData({ ...formData, color })}
                    data-testid={`color-${color}`}
                  />
                ))}
              </div>

              <div className="flex items-center gap-3 mt-3">
                <Label htmlFor="custom-color" className="text-sm text-slate-500">
                  Oder eigene Farbe:
                </Label>
                <Input
                  id="custom-color"
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-12 h-8 p-0 border-0 cursor-pointer"
                  data-testid="custom-color-input"
                />
              </div>
            </div>

            {/* Preview */}
            <div className="space-y-2">
              <Label className="text-sm text-slate-500">Vorschau</Label>
              <div className="p-4 bg-slate-50 rounded-lg">
                <Badge
                  variant="outline"
                  className="text-sm px-3 py-1"
                  style={getTagStyle(formData.color)}
                >
                  {formData.name || "Tag Name"}
                </Badge>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-tag-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Speichern...
                </>
              ) : selectedTag ? (
                "Aktualisieren"
              ) : (
                "Erstellen"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
